/*
 * product-purchase-form.js — Variant resolution + URL push +
 * H3.0 variant:update dispatch + ATC button state machine.
 *
 * Attaches one instance per `.product-purchase-form` on the page.
 * Reads the embedded product-json blob to map selected option
 * values back to a variant; on any option change:
 *
 *   - Updates the form's hidden `id` input to the new variant id.
 *   - Pushes `?variant=<id>` to the URL (replaceState so back
 *     button still works, but no extra history entry per click).
 *   - Dispatches a `variant:update` CustomEvent with the same
 *     payload shape H3.0's native `VariantUpdateEvent` uses
 *     (`event.detail.resource = variant`). The PDP's gallery
 *     (assets/component-main-product-gallery.js) and badges
 *     (assets/product-page-badges.js) both already listen for
 *     this and react.
 *   - Resolves the ATC button state from the variant and applies
 *     it (label / class / type / disabled). Five states:
 *       atc       Add to Cart       (default, type=submit)
 *       preorder  Pre-Order         (type=submit, still buyable)
 *       oos       Out of Stock      (disabled, type=button)
 *       notify    Notify Me ...     (type=button, dispatches
 *                                    `pdp:open-notify`)
 *       enquiry   Make an Enquiry   (type=button, dispatches
 *                                    `pdp:open-enquiry`)
 *     Made-to-order metafield (custom.made_to_order) → enquiry;
 *     preorder metafield (smart.pre_order + no stock) → preorder;
 *     unavailable + no backorders → notify (if modal block
 *     present) or oos; everything else → atc.
 *
 * Initial load — if `?variant=<id>` is in the URL, that variant
 * trumps the server-rendered `selected_or_first_available_variant`
 * and the picker repaints to match (radio / select state is set
 * via the option name → input value lookup). Otherwise the
 * picker uses whatever Shopify already chose server-side. State
 * is applied on first paint either way.
 */

import { formatMoney } from '@theme/money-formatting';

const SELECTOR = '.product-purchase-form';
const INSTANCE_KEY = '_productPurchaseFormInstance';

class ProductPurchaseForm {
  constructor(root) {
    this.root = root;
    this.form = root.querySelector('.product-purchase-form__form');
    this.variantInput = root.querySelector('[data-product-form-variant-id]');
    this.optionInputs = Array.from(root.querySelectorAll('[data-option-select]'));
    this.atcButton = root.querySelector('[data-purchase-form-atc]');
    this.atcLabel = root.querySelector('.product-purchase-form__atc-text');

    // Gift card mode flag — set on the form root by
    // snippets/product-purchase-form-block.liquid. When true,
    // `_applyState` drops the ` - <price>` suffix from the `atc`
    // and `preorder` labels so the button reads a plain
    // "Add to cart" regardless of variant / price. Every other
    // product template renders WITHOUT the data attribute, so
    // `this.isGiftCard` resolves to false and label building
    // works exactly as before.
    this.isGiftCard = this.root.dataset.isGiftCard === 'true';

    this.productJson = this._readProductJson();
    if (!this.productJson) return;

    this.variants = this.productJson.variants || [];
    this.optionsCount = (this.productJson.options || []).length;
    this.mtoMap = this._readMtoMap();
    this.preorderMap = this._readPreorderMap();
    // Modal-presence detection runs in JS rather than server-side.
    // section.blocks is unreliable to iterate from inside both
    // rendered snippets and block files in Horizon — the
    // modal elements being present in the rendered DOM is the
    // authoritative source of truth and JS can read it directly.
    const section = root.closest('.shopify-section');
    const scope = section || document;
    this.enquiryEnabled = !!scope.querySelector('enquiry-modal-component');
    this.notifyEnabled = !!scope.querySelector('notify-me-modal-component');

    this.abortController = new AbortController();
    this._bind();
    this._bindAtcStateClicks();
    this._bindStateRequests();
    this._bindSwatchHoverName();
    this._syncFromUrl();

    // Initial state — `_syncFromUrl` already calls `_setCurrentVariant`
    // when ?variant= is in the URL, which applies the state. Cover the
    // no-URL-param case explicitly so first paint matches the
    // selected_or_first_available_variant Shopify chose server-side.
    const initialId = this.variantInput?.value;
    if (initialId) {
      const initialVariant = this.variants.find(
        (v) => String(v.id) === String(initialId)
      );
      if (initialVariant) {
        this._applyState(this._resolveState(initialVariant), initialVariant);
      }
    }

    // Paint per-option availability on first load so unavailable
    // values are marked before any user interaction. _syncFromUrl
    // / _setCurrentVariant cover the path-from-URL case via the
    // call inside _setCurrentVariant; this catch covers the
    // server-rendered initial state.
    this._updateOptionAvailability();
  }

  _readMtoMap() {
    const node = this.root.querySelector('.product-purchase-form__mto-map');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (_e) {
      return {};
    }
  }

  _readPreorderMap() {
    const node = this.root.querySelector('.product-purchase-form__preorder-map');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (_e) {
      return {};
    }
  }

  _readProductJson() {
    const node = this.root.querySelector('.product-purchase-form__product-json');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (_e) {
      return null;
    }
  }

  _bind() {
    this.optionInputs.forEach((input) => {
      input.addEventListener('change', (event) => this._handleChange(event), {
        signal: this.abortController.signal,
      });
    });
  }

  /**
   * Read the currently-checked / selected value for each option
   * position (1-indexed in Shopify). Returns an array indexed by
   * (position - 1) so `selected[0]` is option 1, etc.
   *
   * Custom Select dropdowns render as <custom-select-component>; the
   * actual value lives on the inner hidden input. The host element
   * itself carries the data-option-* attributes so binding stays
   * generic.
   */
  _readSelectedOptions() {
    const selected = new Array(this.optionsCount).fill(null);
    this.optionInputs.forEach((el) => {
      const pos = parseInt(el.dataset.optionPosition, 10);
      if (!pos) return;
      const idx = pos - 1;

      if (el.tagName === 'CUSTOM-SELECT-COMPONENT') {
        const hidden = el.querySelector('input[type="hidden"]');
        if (hidden) selected[idx] = hidden.value;
      } else if (el.tagName === 'SELECT') {
        selected[idx] = el.value;
      } else if (el.type === 'radio') {
        if (el.checked) selected[idx] = el.value;
      } else {
        // Defensive — handles any other input type Shopify might add later.
        if (el.value) selected[idx] = el.value;
      }
    });
    return selected;
  }

  _findVariant(selectedOptions) {
    if (!selectedOptions.every((v) => v !== null)) return null;
    return (
      this.variants.find((v) => {
        const opts = [v.option1, v.option2, v.option3];
        return selectedOptions.every((val, i) => val === opts[i]);
      }) || null
    );
  }

  _handleChange(event) {
    const selected = this._readSelectedOptions();
    let variant = this._findVariant(selected);

    // No exact match -- the picked combination doesn't exist as a
    // real variant (e.g. the user switched Colour to one that
    // doesn't carry the currently-selected Size). Resolve to a
    // fallback variant that keeps the option the user just
    // changed and shifts the others to whatever's available,
    // then sync the picker UI so what's visible matches what's
    // in the cart.
    if (!variant) {
      variant = this._findFallbackVariant(selected, event);
      if (variant) this._syncPickerToVariant(variant);
    }

    if (!variant) return;
    this._setCurrentVariant(variant);
  }

  /**
   * Resolve a fallback variant when the user's picked option set
   * doesn't map to any real variant. Anchors on the option the
   * user just changed (read from event.target's
   * data-option-position ancestor) and picks the first available
   * variant matching that anchor.
   *
   * Returns null if the changed input can't be identified or no
   * variant carries the anchor value at all (true dead-end --
   * very rare; happens only if the option set is internally
   * inconsistent).
   */
  _findFallbackVariant(selectedOptions, event) {
    const target = event && event.target;
    if (!target) return null;
    const optionEl =
      target.closest && target.closest('[data-option-position]');
    if (!optionEl) return null;
    const pos = parseInt(optionEl.dataset.optionPosition, 10);
    if (!pos) return null;

    const changedIdx = pos - 1;
    const anchor = selectedOptions[changedIdx];
    if (anchor == null) return null;

    const opts = ['option1', 'option2', 'option3'];
    const matching = this.variants.filter(
      (v) => v[opts[changedIdx]] === anchor
    );
    if (matching.length === 0) return null;

    // Prefer an available variant; fall back to the first match
    // so the picker still resolves (state machine will paint
    // 'oos' / 'notify' downstream).
    return matching.find((v) => v.available) || matching[0];
  }

  /**
   * Sync each option input to the value the variant carries for
   * that position. Used after a fallback resolution so the picker
   * UI reflects the variant that's actually going into the cart.
   *
   * custom-select-component is driven through its public
   * `selectByValue()` API (which dispatches a `change` event on
   * its inner hidden input). The bubbled change re-triggers
   * `_handleChange`, but by then the picker matches the variant
   * exactly so the second pass takes the fast path and the calls
   * to `_setCurrentVariant` are idempotent.
   */
  _syncPickerToVariant(variant) {
    const optionValues = [variant.option1, variant.option2, variant.option3];
    this.optionInputs.forEach((el) => {
      const pos = parseInt(el.dataset.optionPosition, 10);
      if (!pos) return;
      const target = optionValues[pos - 1];
      if (target == null) return;

      if (el.tagName === 'CUSTOM-SELECT-COMPONENT') {
        if (typeof el.selectByValue === 'function' && el.value !== target) {
          el.selectByValue(target);
        }
      } else if (el.tagName === 'SELECT') {
        if (el.value !== target) el.value = target;
      } else if (el.type === 'radio') {
        el.checked = el.value === target;
      }
    });
  }

  _setCurrentVariant(variant) {
    if (this.variantInput) this.variantInput.value = variant.id;
    this._pushVariantUrl(variant.id);
    this._dispatchVariantUpdate(variant);
    this._applyState(this._resolveState(variant), variant);
    this._updateOptionAvailability();
    this._updateOptionNames(variant);
    this._updateLowStock(variant);
  }

  /**
   * Show / hide the pre-rendered low-stock notice for the
   * currently-selected variant. The snippet pre-renders one
   * `<p data-variant-id="..." data-low-stock-show="true|false">`
   * per variant; this method walks them, makes the matching
   * item visible IF its `data-low-stock-show` is "true", and
   * collapses the wrapper to display:none when no item
   * qualifies (keeps the 12px margin from leaving an empty
   * padded slot above the ATC row).
   *
   * Same per-variant pattern preorder-notice.js uses, scoped
   * here to this.root rather than a section query so multiple
   * purchase forms on the same page don't cross-talk.
   */
  _updateLowStock(variant) {
    const wrapper = this.root.querySelector('.product-purchase-form__low-stock');
    if (!wrapper) return;
    const id = String(variant.id);
    const items = wrapper.querySelectorAll('[data-variant-id]');
    let anyVisible = false;
    items.forEach((item) => {
      const matches = item.getAttribute('data-variant-id') === id;
      const show = item.getAttribute('data-low-stock-show') === 'true';
      const visible = matches && show;
      item.style.display = visible ? '' : 'none';
      if (visible) anyVisible = true;
    });
    wrapper.style.display = anyVisible ? '' : 'none';
  }

  /**
   * Rewrite the inline "selected value" name next to each
   * swatch option's label (the variant-picker renders one
   * `<span data-current-name data-option-position="N">`
   * per swatch option). Pulls from variant.optionN so the
   * displayed name follows whatever the fallback resolver
   * ultimately committed -- e.g. clicking Chalk Blue on an
   * unavailable size still updates the name to "Chalk Blue"
   * because _findFallbackVariant picks the first Chalk Blue
   * variant and the resulting variant.option1 is that value.
   *
   * Pills + dropdowns never render the name span (no targets)
   * so this is a no-op for non-swatch layouts.
   */
  _updateOptionNames(variant) {
    const opts = ['option1', 'option2', 'option3'];
    this.root.querySelectorAll('[data-current-name][data-option-position]').forEach((el) => {
      const pos = parseInt(el.dataset.optionPosition, 10);
      if (!pos || pos < 1 || pos > 3) return;
      const value = variant[opts[pos - 1]];
      if (value != null) el.textContent = value;
    });
  }

  /**
   * Hover preview for the inline swatch value name. Mouseenter on
   * a swatch label rewrites the matching
   * `[data-current-name][data-option-position]` span to that
   * swatch's option value; mouseleave restores it from the
   * currently-selected variant (NOT a captured-at-init snapshot
   * -- the snapshot would go stale once the user picked a
   * different variant). Mirrors product-group-selector.js's
   * hover-name behaviour, adapted for the PF's dynamic state.
   *
   * Hover only triggers on swatch labels (`.product-purchase-
   * form__swatch`) -- pills + dropdowns don't render the
   * inline name span so the listener wouldn't have a target.
   * Each label's associated radio input is resolved by `for=""`,
   * which also carries data-option-position + value (the source
   * of truth, since the option value text isn't otherwise stored
   * on the label itself).
   */
  _bindSwatchHoverName() {
    const labels = this.root.querySelectorAll('.product-purchase-form__swatch');
    labels.forEach((label) => {
      const inputId = label.getAttribute('for');
      if (!inputId) return;
      const input = this.root.querySelector(`#${CSS.escape(inputId)}`);
      if (!input) return;
      const position = input.dataset.optionPosition;
      const hoveredValue = input.value;
      if (!position || hoveredValue == null) return;

      const nameEl = this.root.querySelector(
        `[data-current-name][data-option-position="${position}"]`
      );
      if (!nameEl) return;

      label.addEventListener(
        'mouseenter',
        () => {
          nameEl.textContent = hoveredValue;
        },
        { signal: this.abortController.signal }
      );

      label.addEventListener(
        'mouseleave',
        () => {
          const currentId = this.variantInput?.value;
          if (!currentId) return;
          const variant = this.variants.find((v) => String(v.id) === String(currentId));
          if (!variant) return;
          const opts = ['option1', 'option2', 'option3'];
          const pos = parseInt(position, 10);
          if (!pos || pos < 1 || pos > 3) return;
          const restored = variant[opts[pos - 1]];
          if (restored != null) nameEl.textContent = restored;
        },
        { signal: this.abortController.signal }
      );
    });
  }

  /**
   * Recompute per-option-value state against the currently-selected
   * variant. For each option input position, walk the possible
   * values and mark each as available / sold-out / unavailable:
   *
   *   available     a real variant exists for this value combined
   *                 with the other current selections, AND that
   *                 variant is purchasable (variant.available, or
   *                 backorders allowed).
   *   sold-out      a real variant exists but isn't purchasable
   *                 (out of stock and no backorder policy).
   *   unavailable   no variant carries this value with the other
   *                 current selections.
   *
   * Why this matters: a product with Colour x Size doesn't always
   * carry every cross, and some crosses that exist are out of
   * stock. Marking both states up front lets the UI signal each
   * differently -- unavailable is non-interactive (the fallback
   * resolver shifts to a real variant), sold-out is selectable so
   * the merchant can show an Out of Stock / Notify Me state on
   * the button after click.
   *
   * Marks:
   *   Radio inputs (swatches / pills)
   *     data-option-state="<state>" on the input. CSS in
   *     component-product-purchase-form.css styles each state.
   *
   *   <custom-select-component> options (<li data-value>)
   *     aria-disabled="true" + data-disabled="true" only on the
   *     unavailable state. Sold-out options stay selectable
   *     because the ATC state machine handles the Out of Stock
   *     label when the variant is picked.
   *
   *   Native <select> <option>
   *     `disabled` property only on the unavailable state.
   */
  _updateOptionAvailability() {
    const currentSelection = this._readSelectedOptions();

    this.optionInputs.forEach((el) => {
      const pos = parseInt(el.dataset.optionPosition, 10);
      if (!pos) return;
      const idx = pos - 1;

      if (el.tagName === 'CUSTOM-SELECT-COMPONENT') {
        const options = el.querySelectorAll('[data-value]');
        options.forEach((opt) => {
          const value = opt.dataset.value;
          if (value == null) return;
          const state = this._variantStateFor(idx, value, currentSelection);
          if (state === 'unavailable') {
            opt.setAttribute('aria-disabled', 'true');
            opt.setAttribute('data-disabled', 'true');
          } else {
            opt.removeAttribute('aria-disabled');
            opt.removeAttribute('data-disabled');
          }
          opt.setAttribute('data-option-state', state);
        });
      } else if (el.tagName === 'SELECT') {
        Array.from(el.options).forEach((opt) => {
          const state = this._variantStateFor(idx, opt.value, currentSelection);
          opt.disabled = state === 'unavailable';
          opt.setAttribute('data-option-state', state);
        });
      } else if (el.type === 'radio') {
        const state = this._variantStateFor(idx, el.value, currentSelection);
        el.setAttribute('data-option-state', state);
        // Disable the radio itself on the unavailable state. The
        // label's pointer events stay enabled (so the CSS
        // `cursor: not-allowed` still surfaces on hover) but a
        // click on a label whose `for` target is disabled is a
        // no-op in every browser -- the radio doesn't toggle and
        // no `change` event fires. Sold-out radios stay enabled
        // because the merchant explicitly wants those clickable
        // so the ATC state machine can flip into Out of Stock /
        // Notify Me.
        el.disabled = state === 'unavailable';
      }
    });
  }

  /**
   * Resolve the per-value state. Three outcomes:
   *   'available'   variant exists AND (available OR backorders OK).
   *   'sold-out'    variant exists AND not purchasable.
   *   'unavailable' no variant matches the value + other selections.
   *
   * Other-position nulls in the selection are tolerated (treated
   * as "don't care") so partial-selection passes (e.g. the
   * initial paint before all options have been touched) still
   * resolve sensibly.
   */
  _variantStateFor(positionIdx, value, currentSelection) {
    const opts = ['option1', 'option2', 'option3'];
    const match = this.variants.find((v) => {
      if (v[opts[positionIdx]] !== value) return false;
      for (let i = 0; i < this.optionsCount; i++) {
        if (i === positionIdx) continue;
        const sel = currentSelection[i];
        if (sel == null) continue;
        if (v[opts[i]] !== sel) return false;
      }
      return true;
    });
    if (!match) return 'unavailable';
    const allowsBackorders =
      match.inventory_management !== 'shopify' ||
      match.inventory_policy === 'continue';
    return match.available || allowsBackorders ? 'available' : 'sold-out';
  }

  /**
   * Resolve the ATC button state from a variant. Mirrors v3.0's
   * sa-product-form precedence, extended with a Pre-Order state:
   *
   *   1. Made-to-order (custom.made_to_order metafield) → 'enquiry'
   *   2. Pre-order (smart.pre_order metafield + no current stock)
   *      → 'preorder'
   *   3. Unavailable + no backorders → 'notify' (when enabled) or 'oos'
   *   4. Otherwise → 'atc'
   *
   * Pre-order check matches the v3.0 preorder-notice snippet's show
   * logic — the flag alone isn't enough; the variant also needs to be
   * out of stock (inventory_quantity <= 0). This way a merchant who
   * leaves the flag on after restocking doesn't get a "Pre-Order"
   * button on an in-stock item.
   *
   * Backorders are allowed when Shopify isn't tracking inventory
   * (inventory_management !== 'shopify') OR the policy is
   * 'continue' (sell when out of stock). When backorders are
   * allowed, an unavailable variant still resolves to 'atc' — the
   * customer can still buy.
   *
   * Modal-dependent states (enquiry, notify) only return when the
   * matching modal block is present in the section; without the
   * modal the state machine falls through to its non-modal sibling
   * (enquiry → atc; notify → oos) so the button stays usable.
   */
  _resolveState(variant) {
    if (!variant) return 'atc';

    const isMto = this.mtoMap[String(variant.id)] === true;
    if (isMto) {
      return this.enquiryEnabled ? 'enquiry' : 'atc';
    }

    const preorder = this.preorderMap[String(variant.id)];
    if (
      preorder &&
      preorder.pre_order === true &&
      (preorder.inventory_quantity == null || preorder.inventory_quantity <= 0)
    ) {
      return 'preorder';
    }

    const allowsBackorders =
      variant.inventory_management !== 'shopify' ||
      variant.inventory_policy === 'continue';

    if (variant.available || allowsBackorders) return 'atc';

    return this.notifyEnabled ? 'notify' : 'oos';
  }

  /**
   * Format a variant's price using the same money format the
   * Liquid SSR pipes through. The Purchase Form root carries
   * `data-money-format` (set to either shop.money_with_currency_
   * format or shop.money_format depending on
   * settings.currency_code_enabled_product_pages); reading it
   * here means the JS-built button text reads identically to the
   * snippet's first-paint output, no flash on init.
   */
  _formatPrice(variant) {
    if (!variant || variant.price == null) return '';
    const moneyFormat = this.root.dataset.moneyFormat || '${{amount}}';
    const currency = this.root.dataset.currency || 'USD';
    return formatMoney(variant.price, moneyFormat, currency);
  }

  /**
   * Apply a resolved state to the ATC button: label, class
   * modifier, click target (type), and disabled. Side-effect-only;
   * the resolved state ID is the input.
   *
   * Class modifiers (.enquiry-mode / .bis-mode / .is-sold-out)
   * give the modal blocks (MP12 / MP13) a stable hook to react
   * to. The actual modal opening is dispatched via
   * `pdp:open-enquiry` / `pdp:open-notify` custom events from
   * `_bindAtcStateClicks`, scoped to the variant in detail.
   */
  _applyState(state, variant) {
    if (!this.atcButton) return;

    this.atcButton.classList.remove(
      'is-sold-out',
      'is-preorder',
      'enquiry-mode',
      'bis-mode'
    );
    this.atcButton.dataset.atcState = state;

    switch (state) {
      case 'enquiry':
        if (this.atcLabel) this.atcLabel.textContent = 'Make an enquiry';
        this.atcButton.type = 'button';
        this.atcButton.disabled = false;
        this.atcButton.classList.add('enquiry-mode');
        this.atcButton.dataset.variantId = variant?.id ?? '';
        break;

      case 'notify':
        // Notify state IS the user-facing "OOS but actionable"
        // surface -- variant is unavailable AND the notify modal
        // block is present in the section. Show the OOS context
        // alongside the action so the user understands both why
        // the button can't add-to-cart AND that clicking still
        // does something.
        if (this.atcLabel) this.atcLabel.textContent = 'Out of stock - Notify me';
        this.atcButton.type = 'button';
        this.atcButton.disabled = false;
        this.atcButton.classList.add('bis-mode');
        this.atcButton.dataset.variantId = variant?.id ?? '';
        break;

      case 'oos':
        // Pure OOS fallback -- variant is unavailable AND no
        // notify modal is wired up. Button is disabled, so we
        // intentionally drop the "Notify me" trailing text from
        // the notify-state label above (no false promise of a
        // working click target).
        if (this.atcLabel) this.atcLabel.textContent = 'Out of stock';
        this.atcButton.type = 'button';
        this.atcButton.disabled = true;
        this.atcButton.classList.add('is-sold-out');
        break;

      case 'preorder':
        if (this.atcLabel) {
          // Gift cards suppress the price suffix per design; every
          // other product keeps "Pre order - <price>". Gift cards
          // don't practically hit this branch (no smart.pre_order
          // metafield configured on gift-card variants), but
          // gating defensively keeps the rule consistent with the
          // `atc` case below.
          this.atcLabel.textContent = this.isGiftCard
            ? 'Pre order'
            : `Pre order - ${this._formatPrice(variant)}`;
        }
        this.atcButton.type = 'submit';
        this.atcButton.disabled = false;
        this.atcButton.classList.add('is-preorder');
        break;

      case 'atc':
      default:
        if (this.atcLabel) {
          // Gift cards render a plain "Add to cart" — the
          // denomination the customer picks IS the price, and the
          // recipient-form ATC gate (see
          // assets/gift-card-recipient-form.js) keeps the button
          // disabled until the required fields are filled. Every
          // other product keeps the "Add to cart - <price>" format
          // so the buyer sees the price they're committing to
          // right on the button.
          this.atcLabel.textContent = this.isGiftCard
            ? 'Add to cart'
            : `Add to cart - ${this._formatPrice(variant)}`;
        }
        this.atcButton.type = 'submit';
        this.atcButton.disabled = false;
        break;
    }

    this._dispatchStateChange(state, variant);
  }

  /**
   * Broadcast the resolved state to siblings that need to mirror
   * the Purchase Form (Sticky ATC, future Quick Add variants, etc.).
   * Bubbling so a section-level listener catches it. Source of
   * truth for ATC state lives here; subscribers don't run the
   * resolver themselves.
   */
  /**
   * Late-subscriber handshake. The Purchase Form dispatches its
   * initial `pdp:state-change` during construction — before
   * later-in-source-order modules (Sticky ATC, future siblings)
   * have attached their document-level listeners. When such a
   * subscriber comes up, it fires `pdp:request-state`; we
   * re-broadcast the current resolved state so its first paint
   * matches without resorting to a setTimeout / microtask delay.
   */
  _bindStateRequests() {
    document.addEventListener(
      'pdp:request-state',
      () => {
        const id = this.variantInput?.value;
        if (!id) return;
        const variant = this.variants.find((v) => String(v.id) === String(id));
        if (!variant) return;
        this._dispatchStateChange(this._resolveState(variant), variant);
      },
      { signal: this.abortController.signal }
    );
  }

  _dispatchStateChange(state, variant) {
    this.root.dispatchEvent(
      new CustomEvent('pdp:state-change', {
        bubbles: true,
        detail: {
          state,
          variant: variant || null,
          productId: this.productJson?.id ?? null,
        },
      })
    );
  }

  /**
   * Click handlers for the enquiry / notify states. Both dispatch
   * a CustomEvent (bubbling) that the matching modal block
   * listens for; the modal pulls the variant out of detail.
   *
   * Bound once at construction and gated on the button's
   * current state class so we don't accidentally hijack ATC
   * submits.
   */
  _bindAtcStateClicks() {
    if (!this.atcButton) return;
    this.atcButton.addEventListener(
      'click',
      (event) => {
        const state = this.atcButton.dataset.atcState;
        if (state !== 'enquiry' && state !== 'notify') return;
        event.preventDefault();

        const variantId = this.variantInput?.value;
        const variant = variantId
          ? this.variants.find((v) => String(v.id) === String(variantId))
          : null;

        const name = state === 'enquiry' ? 'pdp:open-enquiry' : 'pdp:open-notify';
        this.root.dispatchEvent(
          new CustomEvent(name, {
            bubbles: true,
            detail: {
              variant,
              productId: this.productJson?.id,
            },
          })
        );
      },
      { signal: this.abortController.signal }
    );
  }

  _pushVariantUrl(variantId) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variantId));
    window.history.replaceState({}, '', url.toString());
  }

  /**
   * Dispatch a CustomEvent matching H3.0's VariantUpdateEvent
   * shape — `event.detail.resource` holds the variant. Bubbling so
   * section-level listeners (gallery, badges) catch it. Fired on
   * the root element so the listeners can scope by section via
   * `event.target.closest('.shopify-section')`.
   *
   * `data.html` is intentionally an empty Document — H3.0's
   * product-price / product-sku / product-inventory / media-
   * gallery / product-form listeners all call
   * `event.detail.data.html.querySelector(...)`; an empty
   * document satisfies the call, returns null for their
   * selectors, and they exit cleanly via their
   * `if (!result) return;` guards. We DON'T try to feed
   * H3.0's product-price a synthetic priceContainer to swap in
   * because its cached `refs.priceContainer` goes stale — the
   * MutationObserver that's meant to keep refs in sync attaches
   * via `requestIdleCallback` and misses early mutations, so
   * `priceContainer.replaceWith(newNode)` ends up operating on
   * a detached node and the live DOM never updates. Instead,
   * `_updateLivePriceBlocks` below mutates the live
   * priceContainer in place — bypassing H3.0's refs entirely.
   */
  _dispatchVariantUpdate(variant) {
    this._updateLivePriceBlocks(variant);
    this._updateAfterpay(variant);

    const event = new CustomEvent('variant:update', {
      bubbles: true,
      detail: {
        resource: variant,
        sourceId: this.root.dataset.productId || '',
        data: {
          productId: this.productJson.id,
          html: document.implementation.createHTMLDocument(''),
        },
      },
    });
    this.root.dispatchEvent(event);
  }

  /**
   * Mutate every `<product-price>` block belonging to this
   * product directly in the live DOM — set the new price text
   * on `.price` / `.price-item--sale.price` spans, set
   * compare-at text on `.compare-at-price`, and toggle the
   * `.price__hidden` modifier on the two outer wrappers based
   * on whether `compare_at_price > price`. In-place mutation
   * (no clone-then-replace) sidesteps H3.0's refs-going-stale
   * bug — there's nothing to swap, nothing to track. Works for
   * OOS variants too; Shopify always supplies `variant.price`.
   *
   * Money format is read from `data-money-format` on the
   * Purchase Form root (Liquid picks `shop.money_with_
   * currency_format` vs `shop.money_format` based on
   * `settings.currency_code_enabled_product_pages`) so the
   * JS-built text matches the SSR pattern exactly.
   */
  _updateLivePriceBlocks(variant) {
    if (!variant) return;

    const productId = String(this.productJson?.id ?? '');
    if (!productId) return;

    const priceElements = document.querySelectorAll(
      `product-price[data-product-id="${productId}"]`
    );
    if (!priceElements.length) return;

    const moneyFormat = this.root.dataset.moneyFormat || '${{amount}}';
    const currency = this.root.dataset.currency || 'USD';

    const priceText = formatMoney(variant.price, moneyFormat, currency);
    const compareAtRaw = variant.compare_at_price;
    const compareText =
      compareAtRaw && Number(compareAtRaw) > Number(variant.price)
        ? formatMoney(compareAtRaw, moneyFormat, currency)
        : null;
    const onSale = compareText !== null;

    priceElements.forEach((pp) => {
      const regular = pp.querySelector('.price__regular');
      if (regular) {
        regular.classList.toggle('price__hidden', onSale);
        const regSpan = regular.querySelector('.price');
        if (regSpan) regSpan.textContent = priceText;
      }

      const sale = pp.querySelector('.price__sale');
      if (sale) {
        sale.classList.toggle('price__hidden', !onSale);
        const salePrice = sale.querySelector('.price-item--sale.price');
        if (salePrice) salePrice.textContent = priceText;
        const compareAtSpan = sale.querySelector('.compare-at-price');
        if (compareAtSpan && compareText) compareAtSpan.textContent = compareText;
      }
    });
  }

  /**
   * Update the Afterpay installment price (`4x $X with`) on variant
   * change. The afterpay snippet renders the price inside
   * `[data-afterpay-price]` and the rest of the copy as siblings,
   * so we only swap the price text and never touch "4x" / "with" /
   * the icon. Scoped to the section so multiple PDPs on a page
   * don't bleed.
   *
   * Mirrors the Liquid filter chain: `price | divided_by: 4 |
   * money_without_trailing_zeros`. We divide first (integer cents),
   * then format, then strip `.00` so the SSR-vs-JS output reads
   * identical for round-number installments (`$25` not `$25.00`).
   */
  _updateAfterpay(variant) {
    if (!variant) return;

    const section = this.root.closest('.shopify-section') || document;
    const afterpayEls = section.querySelectorAll('[data-afterpay-price]');
    if (!afterpayEls.length) return;

    const moneyFormat = this.root.dataset.moneyFormat || '${{amount}}';
    const currency = this.root.dataset.currency || 'USD';

    const installmentCents = Math.round(Number(variant.price) / 4);
    const formatted = formatMoney(installmentCents, moneyFormat, currency)
      .replace(/\.00(?=\D|$)/, '');

    afterpayEls.forEach((el) => {
      el.textContent = formatted;
    });
  }

  /**
   * URL-driven initial selection. If the page loaded with a
   * `?variant=<id>` param that doesn't match what Shopify picked
   * server-side, repaint the picker to match it. Without this the
   * shared / bookmarked URL would show option A while the form
   * submitted option B.
   */
  _syncFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const urlVariantId = parseInt(params.get('variant'), 10);
    if (!urlVariantId) return;

    const variant = this.variants.find((v) => v.id === urlVariantId);
    if (!variant) return;

    // Update the inputs to match the URL variant.
    const optionValues = [variant.option1, variant.option2, variant.option3];
    this.optionInputs.forEach((el) => {
      const pos = parseInt(el.dataset.optionPosition, 10);
      if (!pos) return;
      const target = optionValues[pos - 1];
      if (target == null) return;

      if (el.tagName === 'SELECT') {
        if (el.value !== target) el.value = target;
      } else if (el.type === 'radio') {
        el.checked = el.value === target;
      }
    });

    // Push state through so gallery + badges pick the right variant
    // on initial paint (they don't otherwise know about ?variant=).
    this._setCurrentVariant(variant);
  }

  destroy() {
    this.abortController.abort();
  }
}

function initAll(root) {
  const scope = root || document;
  scope.querySelectorAll(SELECTOR).forEach((el) => {
    if (el[INSTANCE_KEY]) return;
    el[INSTANCE_KEY] = new ProductPurchaseForm(el);
  });
}

function destroyAll(root) {
  const scope = root || document;
  scope.querySelectorAll(SELECTOR).forEach((el) => {
    if (el[INSTANCE_KEY]) {
      el[INSTANCE_KEY].destroy();
      el[INSTANCE_KEY] = null;
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll());
} else {
  initAll();
}

document.addEventListener('shopify:section:load', (e) => initAll(e.target));
document.addEventListener('shopify:section:unload', (e) => destroyAll(e.target));
